import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaUserMd, 
  FaGraduationCap, 
  FaAward, 
  FaFlask, 
  FaChartLine, 
  FaClipboardList,
  FaBook,
  FaArrowLeft,
  FaDownload,
  FaSpinner,
  FaExclamationTriangle,
  FaUniversity
} from 'react-icons/fa';

// Map domain keys to sections
const domainSectionMap = {
  "MRCP(UK)": "Qualifications",
  "MRCS": "Qualifications",
  "Postgraduate Degrees": "Education",
  "Presentations/Posters": "Research",
  "Publications": "Research",
  "Quality Improvement": "Quality Improvement",
  "Teaching Experience": "Teaching",
  "Training in Teaching": "Teaching",
  "Operative Experience": "Clinical Experience",
  "Surgical Experience": "Clinical Experience",
  "Quality Improvement/Audit": "Quality Improvement",
  "Presentations/Publications": "Research",
  "Leadership and Management": "Leadership",
  "Academia": "Education",
  "Additional Achievements": "Achievements",
  "MSRA: Clinical Problem Solving": "Examination Performance",
  "MSRA: Professional Dilemmas": "Examination Performance",
  "Interview: Commitment to Specialty": "Achievements",
  "Interview: Teamwork & Stress": "Achievements",
  "Interview: Teaching & QI": "Teaching",
  "Interview: EM Knowledge": "Clinical Knowledge",
  "Interview: Clinical Scenarios": "Clinical Knowledge"
};

// Icon mapping for sections
const sectionIconMap = {
  "Clinical Experience": FaUserMd,
  "Education": FaGraduationCap,
  "Qualifications": FaBook,
  "Research": FaFlask,
  "Quality Improvement": FaClipboardList,
  "Teaching": FaGraduationCap,
  "Leadership": FaUserMd,
  "Achievements": FaAward,
  "Clinical Knowledge": FaUserMd,
  "Examination Performance": FaBook
};

const CVGen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cvData, setCvData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    // Check if we have data from location state
    if (location.state?.cvData) {
      setCvData(location.state.cvData);
      setLoading(false);
    } else {
      setError('No CV data available. Please go back and try again.');
      setLoading(false);
    }
  }, [location]);

  const handleGoBack = () => {
    navigate('/pathfinder');
  };

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    // Simulate PDF generation process
    setTimeout(() => {
      setIsGeneratingPDF(false);
      alert('PDF downloading functionality will be implemented here');
    }, 2000);
  };

  // Group domain scores by section
  const groupDomainsBySection = () => {
    if (!cvData || !cvData.domainScores) return {};
    
    const sectionsMap = {};
    
    cvData.domainScores.forEach(domain => {
      const section = domainSectionMap[domain.domainKey] || "Other";
      
      if (!sectionsMap[section]) {
        sectionsMap[section] = [];
      }
      
      sectionsMap[section].push(domain);
    });
    
    return sectionsMap;
  };

  if (loading) {
    return (
      <div className="cv-loading">
        <FaSpinner className="cv-spinner" />
        <p>Loading your CV data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cv-error">
        <FaExclamationTriangle className="cv-error-icon" />
        <p>{error}</p>
        <button onClick={handleGoBack}>Go Back to Pathfinder</button>
      </div>
    );
  }

  const { userProfile, trainingPath, domainScores, totalPoints, totalMaxPoints, overallPercentage } = cvData;
  const sectionedDomains = groupDomainsBySection();
  
  return (
    <div className="cv-page">
      <div className="cv-container">
        <div className="cv-actions">
          <button className="cv-back-button" onClick={handleGoBack}>
            <FaArrowLeft /> Back to Pathfinder
          </button>
          <button 
            className="cv-download-button" 
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? <FaSpinner className="cv-spinner" /> : <FaDownload />} 
            {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF'}
          </button>
        </div>

        <header className="cv-header">
          <h1>{userProfile?.name || 'Medical Professional'}</h1>
          <p className="subtitle">{trainingPath} • {userProfile?.chosenSpecialty || 'Specialty'}</p>
        </header>

        <main className="cv-content">
          {/* Personal Overview Section */}
          <section className="cv-section">
            <div className="section-header">
              <FaUserMd className="section-icon" />
              <h2>Professional Overview</h2>
            </div>
            <div className="section-content">
              <p className="cv-bio">{userProfile?.bio || `Medical professional pursuing ${trainingPath} with interests in ${userProfile?.chosenSpecialty || 'various specialties'}.`}</p>
              
              <div className="overview-stats">
                <div className="stat-item">
                  <h4>Training Pathway</h4>
                  <p>{trainingPath === 'IMT' ? 'Internal Medicine Training' : 
                     trainingPath === 'CST' ? 'Core Surgical Training' : 
                     'Acute Care Common Stem'}</p>
                </div>
                <div className="stat-item">
                  <h4>Overall Progress</h4>
                  <p>{totalPoints}/{totalMaxPoints} points ({overallPercentage}%)</p>
                </div>
                <div className="stat-item">
                  <h4>Current Position</h4>
                  <p>{userProfile?.currentPosition || 'Medical Student'}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Education Section - Add university and year */}
          <section className="cv-section">
            <div className="section-header">
              <FaUniversity className="section-icon" />
              <h2>Education</h2>
            </div>
            <div className="section-content">
              <div className="education-item">
                <h3>{userProfile?.medicalUniversity || 'Medical University'}</h3>
                <p className="timeframe">Year {userProfile?.yearInMedSchool || '–'}</p>
                <p>MBBS Medical Degree</p>
              </div>
            </div>
          </section>

          {/* Render sections based on grouped domain scores */}
          {Object.entries(sectionedDomains).map(([sectionName, domains]) => {
            // Skip rendering Education section from domains since we've added our own above
            if (sectionName === "Education") return null;
            
            const SectionIcon = sectionIconMap[sectionName] || FaAward;
            
            return (
              <section key={sectionName} className="cv-section">
                <div className="section-header">
                  <SectionIcon className="section-icon" />
                  <h2>{sectionName}</h2>
                </div>
                <div className="section-content domain-grid">
                  {domains.map((domain, index) => (
                    <div key={index} className="domain-card">
                      <h3>{domain.domainKey}</h3>
                      <div className="domain-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ 
                              width: `${domain.percentage}%`,
                              backgroundColor: domain.color || '#3b82f6'
                            }}
                          ></div>
                        </div>
                        <p className="progress-text">{domain.progress}/{domain.totalPoints} points</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Contact Information Section */}
          {userProfile && (
            <section className="cv-section contact-section">
              <div className="section-header">
                <h2>Contact Information</h2>
              </div>
              <div className="section-content contact-grid">
                {userProfile.email && (
                  <div className="contact-item">
                    <h4>Email</h4>
                    <p>{userProfile.email}</p>
                  </div>
                )}
                {userProfile.phoneNumber && (
                  <div className="contact-item">
                    <h4>Phone</h4>
                    <p>{userProfile.phoneNumber}</p>
                  </div>
                )}
                {userProfile.location && (
                  <div className="contact-item">
                    <h4>Location</h4>
                    <p>{userProfile.location}</p>
                  </div>
                )}
              </div>
            </section>
          )}
        </main>

        <style jsx>{`
          .cv-page {
            width: 100%;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            background-color: #f7fafc;
            color: #2d3748;
            padding-top: calc(64px + 2rem); /* Account for fixed header */
            padding-bottom: 3rem;
            min-height: 100vh;
          }

          .cv-container {
            width: min(92%, 1200px);
            margin: 0 auto;
            background-color: transparent;
          }

          .cv-actions {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2rem;
          }

          .cv-back-button, .cv-download-button {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1.25rem;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .cv-back-button {
            background: #f1f5f9;
            color: #475569;
            border: 1px solid #e2e8f0;
          }

          .cv-download-button {
            background: #3b82f6;
            color: white;
            border: none;
          }

          .cv-back-button:hover {
            background: #e2e8f0;
          }

          .cv-download-button:hover:not(:disabled) {
            background: #2563eb;
          }

          .cv-download-button:disabled {
            background: #94a3b8;
            cursor: not-allowed;
          }

          .cv-header {
            text-align: center;
            margin-bottom: 2.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 2px solid #f0f0f0;
          }

          .cv-header h1 {
            font-size: 2.25rem;
            color: #1a1a1a;
            margin: 0;
            font-weight: 700;
            letter-spacing: -0.02em;
          }

          .subtitle {
            color: #007BFF;
            font-size: 1.125rem;
            margin: 0.5rem 0 0;
            font-weight: 500;
          }

          .cv-content {
            display: flex;
            flex-direction: column;
            gap: 2rem;
          }

          .cv-section {
            margin-bottom: 0; /* Removed bottom margin since we're using gap in parent */
            background: white;
            border-radius: 12px;
            padding: 2rem;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            border: 1px solid #e2e8f0;
          }

          .section-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1.5rem;
          }

          .section-icon {
            font-size: 1.25rem;
            color: #007BFF;
            background: #f0f7ff;
            padding: 0.75rem;
            border-radius: 8px;
          }

          .section-header h2 {
            font-size: 1.25rem;
            color: #1a1a1a;
            margin: 0;
            font-weight: 600;
          }

          .section-content {
            color: #4a4a4a;
          }

          .cv-bio {
            line-height: 1.6;
            margin-bottom: 1.5rem;
          }

          .overview-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
            margin-top: 1.5rem;
          }

          .stat-item {
            background: #f8fafc;
            padding: 1rem;
            border-radius: 8px;
          }

          .stat-item h4 {
            font-size: 0.875rem;
            color: #64748b;
            margin: 0 0 0.5rem 0;
            font-weight: 500;
          }

          .stat-item p {
            font-size: 1rem;
            color: #1e293b;
            font-weight: 600;
            margin: 0;
          }

          .domain-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 1.5rem;
          }

          .domain-card {
            background: #f8fafc;
            padding: 1.25rem;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .domain-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          }

          .domain-card h3 {
            font-size: 1rem;
            color: #1e293b;
            margin: 0 0 1rem 0;
            font-weight: 600;
          }

          .domain-progress {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .progress-bar {
            height: 6px;
            background: #e2e8f0;
            border-radius: 3px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: #3b82f6;
            border-radius: 3px;
          }

          .progress-text {
            font-size: 0.875rem;
            color: #64748b;
            margin: 0;
            font-weight: 500;
            text-align: right;
          }

          .contact-section {
            background: #f8fafc;
          }

          .contact-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
          }

          .contact-item h4 {
            font-size: 0.875rem;
            color: #64748b;
            margin: 0 0 0.25rem 0;
            font-weight: 500;
          }

          .contact-item p {
            font-size: 1rem;
            color: #1e293b;
            margin: 0;
          }

          .cv-loading, .cv-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
            padding: 2rem;
            text-align: center;
            padding-top: calc(64px + 2rem); /* Match the main container padding */
          }

          .cv-spinner {
            animation: spin 1s linear infinite;
            font-size: 2rem;
            color: #3b82f6;
            margin-bottom: 1rem;
          }

          .cv-error-icon {
            color: #ef4444;
            font-size: 2.5rem;
            margin-bottom: 1rem;
          }

          .cv-error p {
            color: #ef4444;
            margin-bottom: 1.5rem;
            max-width: 400px;
          }

          .cv-error button {
            background: #3b82f6;
            color: white;
            border: none;
            padding: 0.75rem 1.25rem;
            border-radius: 0.5rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .cv-error button:hover {
            background: #2563eb;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          @media (max-width: 768px) {
            .cv-page {
              padding-top: calc(56px + 1.5rem); /* Adjusted for mobile header */
            }
            
            .cv-container {
              width: 94%;
            }

            .cv-header h1 {
              font-size: 1.75rem;
            }

            .cv-section {
              padding: 1.5rem;
            }
            
            .cv-actions {
              flex-direction: column;
              gap: 1rem;
            }
            
            .cv-back-button, .cv-download-button {
              width: 100%;
              justify-content: center;
            }
          }

          .education-item {
            margin-bottom: 1.5rem;
          }

          .education-item h3 {
            font-size: 1.125rem;
            color: #1a1a1a;
            margin: 0 0 0.5rem;
            font-weight: 600;
          }

          .education-item .timeframe {
            color: #007BFF;
            font-size: 0.875rem;
            font-weight: 500;
            margin: 0 0 0.5rem;
          }

          .education-item p {
            color: #4a4a4a;
            margin: 0.5rem 0 0;
            line-height: 1.5;
          }
        `}</style>
      </div>
    </div>
  );
};

export default CVGen;